import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

function AdminTestimonials() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const emptyForm = {
    name: '',
    designation: '',
    company: '',
    testimonial: '',
    rating: 5,
  };
  const [formData, setFormData] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    if (!session?.token) {
      navigate('/admin-login', { state: { fromProtected: true } });
      return null;
    }
    return session.token;
  };

  const fetchTestimonials = async () => {
    const token = getToken();
    if (!token) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/api/admin/testimonials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setTestimonials(res.data.testimonials);
    } catch (err) {
      setApiError('Failed to fetch testimonials.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchTestimonials(); }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setApiError('');
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setPhotoFile(null);
    setPhotoPreview(null);
    setEditingId(null);
    setShowForm(false);
    setApiError('');
    setSuccessMessage('');
  };

  const handleEdit = (t) => {
    setFormData({
      name: t.name || '',
      designation: t.designation || '',
      company: t.company || '',
      testimonial: t.testimonial || '',
      rating: t.rating || 5,
    });
    setPhotoPreview(t.photo ? `${apiUrl}/${t.photo}` : null);
    setEditingId(t.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;

    if (!formData.name.trim() || !formData.testimonial.trim() || !formData.designation.trim()) {
      setApiError('Name, designation, and testimonial are required.');
      return;
    }

    setIsLoading(true);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => data.append(k, v));
      if (photoFile) data.append('photo', photoFile);

      const url = editingId
        ? `${apiUrl}/api/admin/testimonials/${editingId}`
        : `${apiUrl}/api/admin/testimonials`;
      const method = editingId ? 'put' : 'post';

      const res = await axios[method](url, data, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        setSuccessMessage(editingId ? 'Testimonial updated!' : 'Testimonial added!');
        resetForm();
        fetchTestimonials();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setApiError(res.data.message || 'Operation failed.');
      }
    } catch (err) {
      setApiError(err.response?.data?.message || 'Server error.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const token = getToken();
    if (!token) return;
    try {
      await axios.post(
        `${apiUrl}/api/admin/testimonials/${id}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTestimonials();
    } catch {
      setApiError('Failed to update status.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial? This cannot be undone.')) return;
    const token = getToken();
    if (!token) return;
    try {
      await axios.delete(`${apiUrl}/api/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchTestimonials();
      setSuccessMessage('Testimonial deleted.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      setApiError('Failed to delete testimonial.');
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">

          {/* Breadcrumb */}
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Testimonials</span></li>
              </ul>
            </div>
            <button
              className="btn btn-main rounded-pill py-9"
              onClick={() => { resetForm(); setShowForm(true); }}
            >
              <i className="ph ph-plus me-2" /> Add Testimonial
            </button>
          </div>

          {/* Alerts */}
          {apiError && <div className="alert alert-danger mb-20">{apiError}</div>}
          {successMessage && <div className="alert alert-success mb-20">{successMessage}</div>}

          {/* Add / Edit Form */}
          {showForm && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100 flex-between">
                <h5 className="mb-0">{editingId ? 'Edit Testimonial' : 'Add New Testimonial'}</h5>
                <button className="btn btn-sm btn-outline-danger rounded-pill" onClick={resetForm}>
                  Cancel
                </button>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-20">

                    {/* Photo upload */}
                    <div className="col-12">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">
                        Photo <span className="text-gray-400 fw-normal text-13">(Recommended: 200×200px)</span>
                      </label>
                      <div className="d-flex align-items-center gap-16">
                        {photoPreview ? (
                          <img
                            src={photoPreview}
                            alt="preview"
                            style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #2D8653' }}
                          />
                        ) : (
                          <div style={{
                            width: 80, height: 80, borderRadius: '50%', background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '2px dashed #d1d5db'
                          }}>
                            <i className="ph ph-user text-gray-400" style={{ fontSize: 28 }} />
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/jpeg,image/jpg,image/png,image/gif"
                          onChange={handlePhotoChange}
                          style={{ maxWidth: 300 }}
                        />
                      </div>
                    </div>

                    {/* Name */}
                    <div className="col-sm-6">
                      <label htmlFor="name" className="h5 mb-8 fw-semibold font-heading">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        className="form-control py-11"
                        placeholder="e.g. Nat Reynolds"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={isLoading}
                        required
                      />
                    </div>

                    {/* Designation */}
                    <div className="col-sm-6">
                      <label htmlFor="designation" className="h5 mb-8 fw-semibold font-heading">
                        Designation <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="designation"
                        className="form-control py-11"
                        placeholder="e.g. Chief Accountant"
                        value={formData.designation}
                        onChange={handleChange}
                        disabled={isLoading}
                        required
                      />
                    </div>

                    {/* Company */}
                    <div className="col-sm-6">
                      <label htmlFor="company" className="h5 mb-8 fw-semibold font-heading">
                        Company / Organization
                      </label>
                      <input
                        type="text"
                        id="company"
                        className="form-control py-11"
                        placeholder="e.g. ABC Corp"
                        value={formData.company}
                        onChange={handleChange}
                        disabled={isLoading}
                      />
                    </div>

                    {/* Rating */}
                    <div className="col-sm-6">
                      <label htmlFor="rating" className="h5 mb-8 fw-semibold font-heading">
                        Rating (1–5)
                      </label>
                      <select
                        id="rating"
                        className="form-select py-9 text-15"
                        value={formData.rating}
                        onChange={handleChange}
                        disabled={isLoading}
                      >
                        {[5, 4, 3, 2, 1].map((r) => (
                          <option key={r} value={r}>{'★'.repeat(r)}{'☆'.repeat(5 - r)} ({r}/5)</option>
                        ))}
                      </select>
                    </div>

                    {/* Testimonial text */}
                    <div className="col-12">
                      <label htmlFor="testimonial" className="h5 mb-8 fw-semibold font-heading">
                        Testimonial <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="testimonial"
                        className="form-control py-11"
                        rows={4}
                        placeholder="Write the testimonial here..."
                        value={formData.testimonial}
                        onChange={handleChange}
                        disabled={isLoading}
                        required
                      />
                    </div>

                    {/* Submit */}
                    <div className="col-12 flex-align justify-content-end gap-8">
                      <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={resetForm}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-main rounded-pill py-9" disabled={isLoading}>
                        {isLoading ? 'Saving...' : editingId ? 'Update Testimonial' : 'Add Testimonial'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Testimonials Table */}
          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">All Testimonials ({testimonials.length})</h5>
            </div>
            <div className="card-body p-0">
              {isLoading && !showForm ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : testimonials.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No testimonials yet. Add one above.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">#</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Person</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Testimonial</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Rating</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Status</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testimonials.map((t, i) => (
                        <tr key={t.id}>
                          <td className="py-12 px-20 text-15">{i + 1}</td>
                          <td className="py-12 px-20">
                            <div className="d-flex align-items-center gap-12">
                              <img
                                src={t.photo ? `${apiUrl}/${t.photo}` : 'assets/img/testimonials/default-avatar.png'}
                                alt={t.name}
                                style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e8f5ee' }}
                              />
                              <div>
                                <div className="text-15 fw-semibold text-heading">{t.name}</div>
                                <div className="text-13 text-gray-400">{t.designation}{t.company ? ` · ${t.company}` : ''}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-12 px-20" style={{ maxWidth: 320 }}>
                            <p className="text-14 text-gray-500 mb-0" style={{
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                            }}>
                              {t.testimonial}
                            </p>
                          </td>
                          <td className="py-12 px-20">
                            <span style={{ color: '#F5A623', fontSize: 15 }}>
                              {'★'.repeat(t.rating || 5)}{'☆'.repeat(5 - (t.rating || 5))}
                            </span>
                          </td>
                          <td className="py-12 px-20">
                            <button
                              className={`badge border-0 py-4 px-12 rounded-pill text-13 fw-medium ${t.status === 1 ? 'bg-success-100 text-success-600' : 'bg-danger-100 text-danger-600'}`}
                              onClick={() => handleToggleStatus(t.id, t.status)}
                              style={{ cursor: 'pointer' }}
                            >
                              {t.status === 1 ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="py-12 px-20">
                            <div className="d-flex align-items-center gap-8">
                              <button
                                className="btn btn-sm btn-outline-main rounded-pill py-4 px-12"
                                onClick={() => handleEdit(t)}
                              >
                                <i className="ph ph-pencil me-1" /> Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger rounded-pill py-4 px-12"
                                onClick={() => handleDelete(t.id)}
                              >
                                <i className="ph ph-trash me-1" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminTestimonials;